import React, { useEffect, useState, useRef } from "react";
import { bool, shape, arrayOf, string } from "prop-types";
import { connect, useDispatch } from "react-redux";
import Head from "pages/_components/Head";
import MainContainer from "pages/_components/MainContainer";
import { Field, Form, Formik } from "formik";
import { Col } from "react-bootstrap";
import * as Yup from "yup";
import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { goBack, push } from "connected-react-router";

import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { selectors as onboardingSelectors } from "reducers/onboarding";
import { actions as modalActions } from "reducers/modal";
import { REGION_ARG } from "constants.js";
import { validateEmailWithRegex, validateCUIT, validatePhone } from "util/form";

import Checkbox from "pages/_components/Checkbox";
import DateField from "pages/_components/fields/DateField";
import CustomText from "pages/_components/CustomText/CustomText";
import Button from "pages/_components/Button";
import TextField from "pages/_components/fields/TextField";
import Selector from "pages/_components/fields/formik/Selector";
import I18n from "pages/_components/I18n";
import HeaderSimple from "pages/_components/HeaderSimple/HeaderSimple";
import ErrorPopup from "pages/_components/ErrorPopup";
import CustomData from "pages/_components/CustomData/CustomData";
import GridTable from "pages/_components/GridTable/GridTable";
import PhoneInput from "pages/_components/fields/PhoneInput";
import Notification from "pages/_components/Notification";
import { isDesktop, isIOS } from "react-device-detect";
import moment from "moment";
import { usePhoneAreaCodesArg } from "../../../hooks/usePhoneAreaCodesArg";
import EnvironmentStepper from "../_components/EnvironmentStepper";
import EnvironmentHelpSlide from "../_components/EnvironmentHelpSlide";
import EnvironmentWelcomeSlide from "../_components/EnvironmentWelcomeSlide";
import EnvironmentExitSlide from "../_components/EnvironmentExitSlide";
import {
    useComplementaryDataSelectors,
    useGeneralDataSelectors,
    useLocalitySelectors,
    useTaxResidence,
    useUifVerify,
} from "./hooks";
import { useHistoricData } from "../hooks/useHistoricData";

const FORM_ID = "environment.step.generalData";
const PAGE_ID = "StepGeneralData";

const StepGeneralDataRefactor = ({ isMobile, generalDataBackup, generalDataRequest, fetchingLocalities }) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const { status: generalDataRequestStatus, error: generalDataRequestError } = generalDataRequest;
    const [error, setError] = useState("");

    const [helpIsVisible, setHelpIsVisible] = useState(false);
    const [exitMsgIsVisible, setExitMsgIsVisible] = useState(false);
    const [welcomeMsgIsVisible, setWelcomeMsgIsVisible] = useState(false);

    const { complementaryDataSelectorsStatus, complementarySelectors } = useComplementaryDataSelectors(setError);
    const {
        countryListComplementaryData,
        fatcaCategoryList,
        crsOrOcdeCategoryList,
        juridicForm,
    } = complementarySelectors;

    const { selectorsRequest, selectors } = useGeneralDataSelectors(setError);
    const { countryList, stateList } = selectors;
    const { selectorsStatus, selectorsValue, selectorsError } = selectorsRequest;

    const { localityList, onChangeProvince } = useLocalitySelectors(stateList, generalDataBackup);

    const { uifDetails, uifDetailsStatus } = useUifVerify();

    const { taxResidenceDefault } = useTaxResidence();

    const isLoading = [selectorsStatus, complementaryDataSelectorsStatus, uifDetailsStatus].includes("FETCHING");

    const { argAreaCodes } = usePhoneAreaCodesArg();

    const defaultPhoneNumber = generalDataBackup?.phone;
    const defaultPhoneNumberLine = generalDataBackup?.landline;

    const [regulatoryEntityMaxLength, setRegulatoryEntityMaxLength] = useState(null);

    useEffect(() => {
        const maxLength = configUtils.get("regulatory.entity.max.length", 30);
        setRegulatoryEntityMaxLength(maxLength);
    }, []);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        formRef.current.setFieldValue(
            "phone",
            generalDataBackup?.phone ?? { countryCode: "", areaCode: "", value: "" },
        );
        formRef.current.setFieldTouched("phone", false, false);
        formRef.current.setFieldValue(
            "landline",
            generalDataBackup?.landline ?? { countryCode: "", areaCode: "", value: "" },
        );
        formRef.current.setFieldTouched("landline", false, false);
        formRef.current.setFieldValue("companyEmail", generalDataBackup?.companyEmail ?? "");
        formRef.current.setFieldTouched("companyEmail", false, false);
        formRef.current.setFieldValue("internal", generalDataBackup?.internal ?? "");
        formRef.current.setFieldValue("companyEmail", generalDataBackup?.companyEmail ?? "");
        formRef.current.setFieldValue("legalCountry", generalDataBackup?.legalCountry ?? "1");
        formRef.current.setFieldValue("legalProvince", generalDataBackup?.legalProvince ?? "");
        formRef.current.setFieldValue("legalLocality", generalDataBackup?.legalLocality ?? "");
        formRef.current.setFieldValue("legalStreet", generalDataBackup?.legalStreet ?? "");
        formRef.current.setFieldValue("legalStreetNumber", generalDataBackup?.legalStreetNumber ?? "");
        formRef.current.setFieldValue("legalFloor", generalDataBackup?.legalFloor ?? "");
        formRef.current.setFieldValue("legalApartment", generalDataBackup?.legalApartment ?? "");
        formRef.current.setFieldValue("legalPostalCode", generalDataBackup?.legalPostalCode ?? "");
        // Complementary data
        formRef.current.setFieldValue(
            "inscriptionDate",
            generalDataBackup?.inscriptionDate ? moment(generalDataBackup?.inscriptionDate) : "",
        );
        formRef.current.setFieldValue("inscriptionNumber", generalDataBackup?.inscriptionNumber ?? "");
        formRef.current.setFieldValue("regulatoryEntity", generalDataBackup?.regulatoryEntity ?? "");
        formRef.current.setFieldValue("fatca", !!generalDataBackup?.fatcaCountry);
        formRef.current.setFieldValue("fatcaCountry", generalDataBackup?.fatcaCountry);
        formRef.current.setFieldValue("fatcaCategory", generalDataBackup?.fatcaCategory || "");
        formRef.current.setFieldValue("giinNumber", generalDataBackup?.giinNumber || "");
        formRef.current.setFieldValue("crsOrOcde", !!generalDataBackup?.crsOrOcdeCountry);
        formRef.current.setFieldValue("crsOrOcdeCountry", generalDataBackup?.crsOrOcdeCountry || "");
        formRef.current.setFieldValue("crsOrOcdeCategory", generalDataBackup?.crsOrOcdeCategory || "");
        formRef.current.setFieldValue("legalNumber", generalDataBackup?.legalNumber || "");
        formRef.current.setFieldValue("economicGroup", !!generalDataBackup?.groupName);
        formRef.current.setFieldValue("groupName", generalDataBackup?.groupName || "");
        formRef.current.setFieldValue("numOfCompanies", generalDataBackup?.numOfCompanies || "");
    }, [isLoading, generalDataBackup]);

    const hasBackupData = !!generalDataBackup?.cuit;
    const { getBackupDataByStepId } = useHistoricData(hasBackupData);

    useEffect(() => {
        if (generalDataRequestStatus === "SUCCESS") {
            dispatch(environmentActions.setGeneralDataRequest({ status: "INITIAL", value: "", error: null }));
            dispatch(push("/environment/stepTaxData"));
            getBackupDataByStepId("stepTaxData");
        } else if (generalDataRequestStatus === "FAILURE") {
            dispatch(push("/environment/stepError"));

            // setError(generalDataRequestError);
            // dispatch(modalActions.modalShow());
        }
    }, [generalDataRequestStatus, generalDataRequestError]);

    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (selectorsStatus === "SUCCESS") {
            // allFormik.setFieldValue("isPosition", "true", false);
            formRef.current.setFieldValue("cuit", selectorsValue?.documentNumber ?? "0000000");
            formRef.current.setFieldValue("companyName", selectorsValue?.businessName ?? "S/N");
            formRef.current.setFieldTouched("cuit", false, false);
            formRef.current.setFieldTouched("companyName", false, false);
        }
    }, [isLoading, selectorsStatus, selectorsValue, selectorsError]);

    const onChangeLocality = (selectedLocality, setFieldValue) => {
        const locality = localityList.find((o) => o.value === selectedLocality);
        setFieldValue("localityName", locality.label);
    };

    const validateForm = (val) => {
        if (val === "" || val === null || val === undefined) {
            return false;
        }
        if (val.split("").filter((x) => x === "9").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "8").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "7").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "6").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "5").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "4").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "3").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "2").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "1").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "0").length > 6) {
            return false;
        }
        return true;
    };
    const submitIsAllowed = ({ values }) => {
        const {
            landline,
            // internal,
            phone,
            companyEmail,
            legalCountry,
            legalProvince,
            legalLocality,
            legalStreet,
            legalStreetNumber,
            // legalFloor,
            // legalApartment,
            legalPostalCode,
            fatca,
            fatcaCategory,
            giinNumber,
            crsOrOcde,
            crsOrOcdeCountry,
            crsOrOcdeCategory,
            legalNumber,
            economicGroup,
            groupName,
            numOfCompanies,
        } = values;

        const firstFieldsAreValid =
            !!landline.value &&
            !!phone.value &&
            !!validateEmailWithRegex(companyEmail) &&
            validateForm(phone.value) &&
            validateForm(landline.value);

        const secondFieldsAreValid =
            !!legalCountry &&
            !!legalProvince &&
            !!legalLocality &&
            !!legalStreet &&
            !!legalStreetNumber &&
            !!legalPostalCode;

        if (fatca && (!fatcaCategory || !giinNumber)) {
            return false;
        }

        if (crsOrOcde && (!crsOrOcdeCountry || !crsOrOcdeCategory || !legalNumber)) {
            return false;
        }

        if (economicGroup && (!groupName || !numOfCompanies)) {
            return false;
        }

        return firstFieldsAreValid && secondFieldsAreValid;
    };

    const onChangeCheckbox = (checkBoxId, allFormik, newVal) => {
        if (!newVal && checkBoxId === "fatca") {
            allFormik.setFieldValue("fatcaCountry", "", false);
            allFormik.setFieldValue("fatcaCategory", "", false);
            allFormik.setFieldValue("giinNumber", "", false);
            allFormik.setFieldTouched("fatcaCountry", false, false);
            allFormik.setFieldTouched("fatcaCategory", false, false);
            allFormik.setFieldTouched("giinNumber", false, false);
        } else if (!newVal && checkBoxId === "crsOrOcde") {
            allFormik.setFieldValue("crsOrOcdeCountry", "", false);
            allFormik.setFieldValue("crsOrOcdeCategory", "", false);
            allFormik.setFieldValue("legalNumber", "", false);
            allFormik.setFieldTouched("crsOrOcdeCountry", false, false);
            allFormik.setFieldTouched("crsOrOcdeCategory", false, false);
            allFormik.setFieldTouched("legalNumber", false, false);
        } else if (!newVal && checkBoxId === "economicGroup") {
            allFormik.setFieldValue("groupName", "", false);
            allFormik.setFieldValue("numOfCompanies", "", false);
            allFormik.setFieldTouched("groupName", false, false);
            allFormik.setFieldTouched("numOfCompanies", false, false);
        }
    };

    const requiredString = i18nUtils.get("forms.inputFile.massivePayments.invalid.required");

    const numberTest = (maxLength) =>
        Yup.string()
            .required(requiredString)
            .test(
                "format-number",
                i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                (value) =>
                    !Number.isNaN(Number(value)) &&
                    !value?.includes(" ") &&
                    (maxLength ? value?.length === maxLength : true),
            );

    const defaultValues = {
        cuit: selectorsValue?.documentNumber || "",
        companyName: selectorsValue?.businessName || "",
        landline: generalDataBackup?.landline ?? { countryCode: "", areaCode: "", value: "" },
        internal: generalDataBackup?.internal ?? "",
        phone: generalDataBackup?.phone ?? { countryCode: "", areaCode: "", value: "" },
        companyEmail: generalDataBackup?.companyEmail ?? "",
        // taxCountry: taxResidenceDefault?.country || "",
        // taxProvince: taxResidenceDefault?.province || "",
        // taxLocality: taxResidenceDefault?.locality || "",
        // taxStreet: taxResidenceDefault?.street || "",
        // taxStreetNumber: taxResidenceDefault?.streetNumber || "",
        // taxFloor: taxResidenceDefault?.floor || "",
        // taxApartment: taxResidenceDefault?.apartment || "",
        // taxPostalCode: taxResidenceDefault?.postalCode || "",
        legalCountry: generalDataBackup?.country || "1",
        legalProvince: generalDataBackup?.province || "",
        legalLocality: generalDataBackup?.locality || "",
        legalStreet: generalDataBackup?.street || "",
        legalStreetNumber: generalDataBackup?.streetNumber || "",
        legalFloor: generalDataBackup?.floor || "",
        legalApartment: generalDataBackup?.apartment || "",
        legalPostalCode: generalDataBackup?.postalCode || "",
        // Complementary data
        inscriptionDate: generalDataBackup?.inscriptionDate ? moment(generalDataBackup?.inscriptionDate) : "",
        inscriptionNumber: generalDataBackup?.inscriptionNumber || "",
        regulatoryEntity: generalDataBackup?.regulatoryEntity || "",
        fatca: !!generalDataBackup?.fatcaCountry,
        fatcaCountry: generalDataBackup?.fatcaCountry,
        fatcaCategory: generalDataBackup?.fatcaCategory || "",
        giinNumber: generalDataBackup?.giinNumber || "",
        crsOrOcde: !!generalDataBackup?.crsOrOcdeCountry,
        crsOrOcdeCountry: generalDataBackup?.crsOrOcdeCountry || "",
        crsOrOcdeCategory: generalDataBackup?.crsOrOcdeCategory || "",
        legalNumber: generalDataBackup?.legalNumber || "",
        economicGroup: !!generalDataBackup?.groupName,
        groupName: generalDataBackup?.groupName || "",
        numOfCompanies: generalDataBackup?.numOfCompanies || "",
    };

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: defaultValues,
        validationSchema: () =>
            Yup.lazy((values) =>
                Yup.object().shape({
                    cuit: Yup.string()
                        .required(i18nUtils.get("onboarding.step.verifyCUIT.cuit.error.empty"))
                        .test(
                            "format-cuit",
                            i18nUtils.get("onboarding.step.verifyCUIT.cuit.error.invalidFormat"),
                            (value) => validateCUIT(value),
                        ),
                    companyName: Yup.string().required(requiredString),
                    landline: validatePhone(argAreaCodes),
                    internal: values.internal
                        ? Yup.string().test(
                              "format-internal",
                              i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                              (value) => !value || !Number.isNaN(Number(value)),
                          )
                        : Yup.string().notRequired(),
                    phone: validatePhone(argAreaCodes),
                    companyEmail: Yup.string()
                        .email(i18nUtils.get("onboarding.step.verifyEmail.email.error.invalidFormat"))
                        .required(requiredString)
                        .test(
                            "format",
                            i18nUtils.get("onboarding.step.verifyEmail.email.error.invalidFormat"),
                            (value) => validateEmailWithRegex(value),
                        ),

                    // taxCountry: Yup.string().required(requiredString),
                    // taxProvince: Yup.string().required(requiredString),
                    // taxLocality: Yup.string().required(requiredString),
                    // taxStreet: Yup.string().required(requiredString),
                    // taxStreetNumber: Yup.string().required(requiredString),
                    // taxFloor: Yup.string().required(requiredString),
                    // taxApartment: Yup.string().required(requiredString),
                    // taxPostalCode: Yup.string()
                    //     .required(requiredString)
                    //     .test(
                    //         "format-streetNumber",
                    //         i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                    //         (value) => !Number.isNaN(Number(value)),
                    //     ),

                    legalCountry: Yup.string().notRequired(),
                    legalProvince: Yup.string().notRequired(),
                    legalLocality: Yup.string().notRequired(),
                    legalStreet: Yup.string().notRequired(),
                    legalStreetNumber: Yup.string().notRequired(),
                    legalFloor: Yup.string().notRequired(),
                    legalApartment: Yup.string().notRequired(),
                    legalPostalCode: Yup.string().notRequired(),
                    // .test(
                    //     "format-streetNumber",
                    //     i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                    //     (value) => !Number.isNaN(Number(value)),
                    // ),
                    // Complementary data
                    inscriptionDate: values.inscriptionDate
                        ? Yup.string().test(
                              "not-future-date",
                              i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                              (value) => new Date(value) <= new Date(),
                          )
                        : Yup.string().notRequired(),
                    inscriptionNumber: values.inscriptionNumber
                        ? Yup.string().test(
                              "format-number",
                              i18nUtils.get("forms.inputFile.massivePayments.invalid.field"),
                              (value) => !Number.isNaN(Number(value)) && !value?.includes(" "),
                          )
                        : Yup.string().notRequired(),
                    regulatoryEntity: Yup.string()
                        .notRequired()
                        .max(
                            regulatoryEntityMaxLength,
                            i18nUtils.get(
                                "regulatory.entity.invalid.error",
                                "Ente regulador puede tener hasta 30 caracteres",
                            ),
                        ),
                    fatca: Yup.bool().notRequired(),
                    // fatcaCountry: Yup.string().when("fatca", {
                    //     is: true,
                    //     then: Yup.string().required(requiredString),
                    // }),
                    fatcaCategory: Yup.string().when("fatca", {
                        is: true,
                        then: Yup.string().required(requiredString),
                    }),
                    giinNumber: Yup.string().when("fatca", {
                        is: true,
                        then: numberTest(),
                    }),
                    crsOrOcde: Yup.bool().notRequired(),
                    crsOrOcdeCountry: Yup.string().when("crsOrOcde", {
                        is: true,
                        then: Yup.string().required(requiredString),
                    }),
                    crsOrOcdeCategory: Yup.string().when("crsOrOcde", {
                        is: true,
                        then: Yup.string().required(requiredString),
                    }),
                    legalNumber: Yup.string().when("crsOrOcde", {
                        is: true,
                        then: numberTest(),
                    }),
                    economicGroup: Yup.bool().notRequired(),
                    groupName: Yup.string().when("economicGroup", {
                        is: true,
                        then: Yup.string().required(requiredString),
                    }),
                    numOfCompanies: Yup.string().when("economicGroup", {
                        is: true,
                        then: numberTest(),
                    }),
                }),
            ),
        onSubmit: (values) => {
            const valueToSave = {
                ...values,
                fatcaCountry: values.fatca
                    ? countryListComplementaryData.find((c) => c.label === "Estados Unidos").value
                    : "",
                landline: values.landline,
                phone: values.phone,
                juridicForm,
            };
            dispatch(environmentActions.setGeneralDataBackup(valueToSave));
            dispatch(environmentActions.saveGeneralData(valueToSave, uifDetails));
        },
    };

    const onHeaderBack = () => {
        dispatch(goBack());
    };

    return (
        <>
            {isMobile ? (
                <Head
                    titleText={i18nUtils.get("legalPerson.title", "Registro")}
                    onBack={onHeaderBack}
                    hideNavbarInMobile
                    tooltipMobile
                    onClickTooltip={() => setHelpIsVisible(true)}
                    tooltipImage={isIOS ? "images/figma/icon/help-white.svg" : "images/figma/icon/help.svg"}
                    showCenterContent
                />
            ) : (
                <HeaderSimple inverse />
            )}
            <Notification scopeToShow="environment" />
            <MainContainer showLoader={isLoading} className="onboarding">
                <EnvironmentStepper isMobile={isMobile} page="StepGeneralData" />
                <div className="step step-general-data">
                    {isMobile && (
                        <I18n id="environment.generalData.title" defaultValue="Datos generales" component="h1" />
                    )}
                    {!isMobile && (
                        <Head
                            titleText={i18nUtils.get("", "Datos generales")}
                            onBack={onHeaderBack}
                            tooltip
                            showCenterContent
                            onClickTooltip={() => setHelpIsVisible(true)}
                            tooltipImage="images/figma/icon/help.svg"
                        />
                    )}
                    <Formik {...formikProps} ref={formRef}>
                        {(allFormik) => (
                            <Form noValidate>
                                <div className="grid-table grid-table-form">
                                    <div className="grid-table-row grid-cols-12">
                                        {/* // White card */}
                                        <div className="grid-table-data col-start-2 col-span-5">
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="cuit"
                                                labelTextDefault={i18nUtils.get("", "CUIT")}
                                                disabled
                                                onlyNumbers
                                                hidePlaceholder
                                            />
                                        </div>
                                        <div className="grid-table-data col-start-7 col-span-5">
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="companyName"
                                                labelTextDefault={i18nUtils.get("", "Denominación social")}
                                                disabled
                                                hidePlaceholder
                                            />
                                        </div>
                                    </div>
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-10 row-start-1 lg:col-start-2 lg:col-span-5">
                                            <div className="grid-table-row grid-cols-12 lg:grid-cols-5 lg:!gap-4">
                                                <div className="grid-table-data col-start-1 col-span-9 row-start-1 lg:col-start-1 lg:col-span-4">
                                                    <Field
                                                        component={PhoneInput}
                                                        idForm={FORM_ID}
                                                        name="landline"
                                                        country={REGION_ARG}
                                                        mobilePhone={defaultPhoneNumberLine || "54"}
                                                        enableSearch={false}
                                                        onlyCountries={["ar"]}
                                                        preferredCountries={["ar"]}
                                                        labelTextDefault={i18nUtils.get("", "Teléfono fijo")}
                                                        hintLabel={i18nUtils.get(
                                                            "",
                                                            "Debés incluir el código de área y luego tu nº de teléfono.",
                                                        )}
                                                        // placeholder="(0) 00 0000-0000"
                                                    />
                                                </div>
                                                <div className="grid-table-data col-start-10 col-span-3 lg:col-start-5 lg:col-span-1">
                                                    <Field
                                                        component={TextField}
                                                        idForm={FORM_ID}
                                                        name="internal"
                                                        labelTextDefault={i18nUtils.get("", "Interno")}
                                                        placeholder="000"
                                                        maxLength={3}
                                                        onlyNumbers
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="grid-table-data md:col-start-2 md:col-span-10 md:row-start-2 lg:row-start-1 lg:col-start-7 lg:col-span-5">
                                            <Field
                                                component={PhoneInput}
                                                idForm={FORM_ID}
                                                name="phone"
                                                country={REGION_ARG}
                                                mobilePhone={defaultPhoneNumber || "54"}
                                                enableSearch={false}
                                                onlyCountries={["ar"]}
                                                preferredCountries={["ar"]}
                                                labelTextDefault={i18nUtils.get("", "Celular")}
                                                hintLabel={i18nUtils.get(
                                                    "",
                                                    "Debés incluir el código de área y luego tu nº de teléfono.",
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-5">
                                            <Field
                                                autoComplete="off"
                                                component={TextField}
                                                idForm={FORM_ID}
                                                maxLength={50}
                                                name="companyEmail"
                                                type="email"
                                                labelTextDefault={i18nUtils.get("", "Email coorporativo")}
                                                placeholder="ejemplo@tuempresa.com"
                                            />
                                        </div>
                                    </div>
                                    {!isMobile && <div style={{ paddingBottom: "45px" }} />}
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-10 subtitle">
                                            <I18n id="" defaultValue="Domicilio fiscal" component="h2" />
                                        </div>
                                    </div>
                                    {!isMobile && <div style={{ paddingTop: "16px" }} />}
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-10">
                                            <GridTable className="box box-shadow">
                                                <GridTable.Container
                                                    className="gap-6"
                                                    columns="4"
                                                    columnsTemplate="fixed">
                                                    <GridTable.Data columnStart={1}>
                                                        <CustomData
                                                            titleText={`${i18nUtils.get(
                                                                "onboarding.personalData.country.label",
                                                                "País",
                                                            )}`}
                                                            valueText={taxResidenceDefault?.country}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={3} columnWidth={2}>
                                                        <CustomData
                                                            titleText={`${i18nUtils.get(
                                                                "onboarding.personalData.province.label",
                                                                "Provincia",
                                                            )}`}
                                                            valueText={taxResidenceDefault?.province}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={1} columnWidth={2}>
                                                        <CustomData
                                                            titleText={`${i18nUtils.get(
                                                                "onboarding.personalData.locality.label",
                                                                "Localidad",
                                                            )}`}
                                                            valueText={taxResidenceDefault?.locality}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={3} columnWidth={2}>
                                                        <CustomData
                                                            titleText={`${i18nUtils.get("", "Dirección")}`}
                                                            valueText={taxResidenceDefault?.address}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={1}>
                                                        <CustomData
                                                            titleText={`${i18nUtils.get(
                                                                "onboarding.personalData.postalCode.label",
                                                                "Código postal",
                                                            )}`}
                                                            valueText={taxResidenceDefault?.postalCode}
                                                        />
                                                    </GridTable.Data>
                                                </GridTable.Container>
                                            </GridTable>
                                        </div>
                                        <div className="grid-table-data col-start-2 col-10">
                                            <>
                                                <I18n id="" defaultValue="Domicilio legal" component="h2" />
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="legalCountry"
                                                    options={countryList || [{ value: "1", label: "Argentina" }]}
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.country.label",
                                                        "País",
                                                    )}`}
                                                    placeholder={`${i18nUtils.get("", "Seleccionar")}`}
                                                    disabled
                                                />
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="legalProvince"
                                                    onChange={(option) =>
                                                        onChangeProvince(option, allFormik.setFieldValue)
                                                    }
                                                    options={
                                                        selectorsStatus === "FETCHING"
                                                            ? [{ value: "1", label: "Cargando..." }]
                                                            : stateList
                                                    }
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.province.label",
                                                        "Provincia",
                                                    )}`}
                                                    placeholder={`${i18nUtils.get("", "Seleccionar")}`}
                                                />
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="legalLocality"
                                                    onChange={(option) =>
                                                        onChangeLocality(option, allFormik.setFieldValue)
                                                    }
                                                    options={
                                                        !localityList?.[0]?.value
                                                            ? [{ value: "1", label: "Cargando..." }]
                                                            : localityList
                                                    }
                                                    label={`${i18nUtils.get(
                                                        "onboarding.personalData.locality.label",
                                                        "Localidad",
                                                    )}`}
                                                    placeholder={`${i18nUtils.get("", "Seleccionar")}`}
                                                    disabled={
                                                        !allFormik.values.legalProvince ||
                                                        (allFormik.values.legalProvince && fetchingLocalities)
                                                    }
                                                />
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="legalStreet"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.street.label",
                                                        "Calle",
                                                    )}`}
                                                    hidePlaceholder
                                                    hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                                />
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="legalStreetNumber"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.streetNumber.label",
                                                        "Número",
                                                    )}`}
                                                    hidePlaceholder
                                                    onlyNumbers
                                                />
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="legalFloor"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.floor.label",
                                                        "Piso",
                                                    )}`}
                                                    onlyNumbers
                                                    hidePlaceholder
                                                    maxLength={2}
                                                />
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="legalApartment"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.apartment.label",
                                                        "Departamento",
                                                    )}`}
                                                    hidePlaceholder
                                                    hasRegex="^[A-Za-z0-9\-_\. ]*$"
                                                    maxLength={3}
                                                />
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="legalPostalCode"
                                                    labelTextDefault={`${i18nUtils.get(
                                                        "onboarding.personalData.postalCode.label",
                                                        "Código postal",
                                                    )}`}
                                                    hidePlaceholder
                                                    onlyNumbers
                                                />
                                            </>
                                        </div>
                                    </div>
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-5 subtitle">
                                            <I18n
                                                id="environment.complementaryData.inscription.title"
                                                defaultValue="Inscripción registral"
                                                component="h2"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-5">
                                            <Field
                                                component={DateField}
                                                idForm={FORM_ID}
                                                idField={`${FORM_ID}.inscriptionDate`}
                                                name="inscriptionDate"
                                                labelKey="environment.complementaryData.inscriptionDate.label"
                                                labelText="Fecha de inscripción (opcional)"
                                                minDate={null}
                                                maxDate={new Date()}
                                            />
                                        </div>
                                        <div className="grid-table-data col-start-7 col-span-5">
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="inscriptionNumber"
                                                labelTextDefault={i18nUtils.get(
                                                    "environment.complementaryData.inscriptionNumber.label",
                                                    "Número de inscripción (opcional)",
                                                )}
                                                placeholder={i18nUtils.get("", "Escribir número")}
                                                onlyNumbers
                                            />
                                        </div>
                                        <div className="grid-table-data col-start-2 col-span-5">
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="regulatoryEntity"
                                                labelTextDefault={i18nUtils.get(
                                                    "environment.complementaryData.regulatoryEntity.label",
                                                    "Ente regulador (opcional)",
                                                )}
                                                placeholder={i18nUtils.get("", "Escribir nombre del ente")}
                                                maxLength={regulatoryEntityMaxLength}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ paddingBottom: "20px" }} />
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-10 subtitle">
                                            <I18n id="" defaultValue="Datos adicionales" component="h2" />
                                        </div>
                                        <div className="grid-table-data col-start-2 col-span-10">
                                            <ul className="list">
                                                <li>
                                                    <Field
                                                        component={Checkbox}
                                                        idForm={FORM_ID}
                                                        name="fatca"
                                                        labelText="environment.regulatoryData.fatca.label"
                                                        labelTextProps={{
                                                            defaultValue:
                                                                "¿Mi empresa es residente fiscal en Estados Unidos (FATCA)?",
                                                        }}
                                                        onChange={(v) => onChangeCheckbox("fatca", allFormik, v)}
                                                    />
                                                    {!!allFormik?.values?.fatca && (
                                                        <div className="grid-table mb-7 pt-6">
                                                            <div className="grid-table-row gap-4 grid-cols-12">
                                                                <div className="grid-table-data col-start-1 col-span-6">
                                                                    <Field
                                                                        component={Selector}
                                                                        idForm={FORM_ID}
                                                                        name="fatcaCountry"
                                                                        // options={countryListComplementaryData}
                                                                        label={`${i18nUtils.get(
                                                                            "",
                                                                            "País de residencia",
                                                                        )}`}
                                                                        placeholder="Estados Unidos"
                                                                        disabled
                                                                    />
                                                                </div>
                                                                <div className="grid-table-data col-start-7 col-span-6">
                                                                    <Field
                                                                        component={Selector}
                                                                        idForm={FORM_ID}
                                                                        name="fatcaCategory"
                                                                        options={fatcaCategoryList}
                                                                        label={`${i18nUtils.get(
                                                                            "",
                                                                            "Categoría FATCA",
                                                                        )}`}
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.personalData.selectorPlaceholder",
                                                                            "Elegir una opción",
                                                                        )}`}
                                                                    />
                                                                </div>
                                                                <div className="grid-table-data col-span-6">
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="giinNumber"
                                                                        labelTextDefault={i18nUtils.get(
                                                                            "",
                                                                            "Nº de GIIN",
                                                                        )}
                                                                        hidePlaceholder
                                                                        onlyNumbers
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </li>
                                                <li>
                                                    <Field
                                                        component={Checkbox}
                                                        idForm={FORM_ID}
                                                        name="crsOrOcde"
                                                        labelText="environment.regulatoryData.crsOrOcde.label"
                                                        labelTextProps={{
                                                            defaultValue:
                                                                "¿Mi empresa es residente fiscal en otro país distinto a Estados Unidos (CRS/OCDE)?",
                                                        }}
                                                        onChange={(v) => onChangeCheckbox("crsOrOcde", allFormik, v)}
                                                    />
                                                    {!!allFormik?.values?.crsOrOcde && (
                                                        <div className="grid-table mb-7 pt-6">
                                                            <div className="grid-table-row gap-4 grid-cols-12">
                                                                <div className="grid-table-data col-start-1 col-span-6">
                                                                    <Field
                                                                        component={Selector}
                                                                        idForm={FORM_ID}
                                                                        name="crsOrOcdeCountry"
                                                                        options={countryListComplementaryData.filter(
                                                                            (c) => c.label !== "Estados Unidos",
                                                                        )}
                                                                        label={`${i18nUtils.get(
                                                                            "",
                                                                            "País de residencia",
                                                                        )}`}
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.personalData.selectorPlaceholder",
                                                                            "Elegir una opción",
                                                                        )}`}
                                                                    />
                                                                </div>
                                                                <div className="grid-table-data col-start-7 col-span-6">
                                                                    <Field
                                                                        component={Selector}
                                                                        idForm={FORM_ID}
                                                                        name="crsOrOcdeCategory"
                                                                        options={crsOrOcdeCategoryList}
                                                                        label={`${i18nUtils.get(
                                                                            "",
                                                                            "Categoría CRS / OCDE",
                                                                        )}`}
                                                                        placeholder={`${i18nUtils.get(
                                                                            "onboarding.personalData.selectorPlaceholder",
                                                                            "Elegir una opción",
                                                                        )}`}
                                                                    />
                                                                </div>
                                                                <div className="grid-table-data col-start-1 col-span-6">
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="legalNumber"
                                                                        labelTextDefault={i18nUtils.get(
                                                                            "",
                                                                            "Nº de identificación fiscal (NIF)",
                                                                        )}
                                                                        hidePlaceholder
                                                                        onlyNumbers
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </li>
                                                <li>
                                                    <Field
                                                        component={Checkbox}
                                                        idForm={FORM_ID}
                                                        name="economicGroup"
                                                        labelText="environment.complementaryData.economicGroup.label"
                                                        labelTextProps={{
                                                            defaultValue: "¿Mi empresa pertenece a un grupo económico?",
                                                        }}
                                                        onChange={(v) =>
                                                            onChangeCheckbox("economicGroup", allFormik, v)
                                                        }
                                                    />
                                                    {!!allFormik?.values?.economicGroup && (
                                                        <div className="grid-table mb-7 pt-6">
                                                            <div className="grid-table-row gap-4 grid-cols-12">
                                                                <div className="grid-table-data col-start-1 col-span-6">
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="groupName"
                                                                        labelTextDefault={i18nUtils.get(
                                                                            "",
                                                                            "Nombre del grupo",
                                                                        )}
                                                                        placeholder=""
                                                                    />
                                                                </div>
                                                                <div className="grid-table-data col-start-7 col-span-6">
                                                                    <Field
                                                                        component={TextField}
                                                                        idForm={FORM_ID}
                                                                        name="numOfCompanies"
                                                                        labelTextDefault={i18nUtils.get(
                                                                            "",
                                                                            "Cantidad de empresas que pertenecen",
                                                                        )}
                                                                        placeholder=""
                                                                        onlyNumbers
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-10">
                                            {!!uifDetails[0] && (
                                                <I18n
                                                    id="environment.uifDetails.statusRegulatedEntity"
                                                    defaultValue="Condición de Sujeto Obligado ante UIF"
                                                    component="h2"
                                                />
                                            )}
                                            {uifDetails.map((uifDetail) => (
                                                <Col
                                                    xs={6}
                                                    md={4}
                                                    mdOffset={2}
                                                    className="box box-border-soft"
                                                    style={{ padding: "20px" }}>
                                                    <h3>
                                                        <CustomText
                                                            text={i18nUtils
                                                                .get("", "Tipo de Sujeto Obligado: ")
                                                                .concat(uifDetail.tipoSujeto)}
                                                        />
                                                    </h3>
                                                    <br />
                                                    <h4>
                                                        <CustomText
                                                            text={i18nUtils
                                                                .get("", "Estado: ")
                                                                .concat(uifDetail.estado)}
                                                        />
                                                    </h4>
                                                </Col>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className={`grid-table form-actions ${!isDesktop && "mt-8"}`}>
                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                        {/* // White card */}
                                        <div className="grid-table-data md:col-start-5 md:col-span-4">
                                            <Button
                                                bsStyle="primary"
                                                label="global.continue"
                                                loading={generalDataRequestStatus === "FETCHING"}
                                                type="submit"
                                                block
                                                disabled={!submitIsAllowed(allFormik)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>

            <EnvironmentHelpSlide
                slideIsVisible={helpIsVisible}
                closeSlide={() => setHelpIsVisible(false)}
                isMobile={isMobile}
                page={PAGE_ID}
            />
            <EnvironmentWelcomeSlide
                slideIsVisible={welcomeMsgIsVisible}
                closeSlide={() => setWelcomeMsgIsVisible(false)}
                isMobile={isMobile}
            />
            <EnvironmentExitSlide
                slideIsVisible={exitMsgIsVisible}
                closeSlide={() => setExitMsgIsVisible(false)}
                isMobile={isMobile}
            />
            <ErrorPopup error={error} handleClick={() => dispatch(modalActions.modalClose())} />
        </>
    );
};

StepGeneralDataRefactor.propTypes = {
    isMobile: bool.isRequired,
    generalDataBackup: arrayOf(shape({})).isRequired,
    generalDataRequest: arrayOf(shape({})).isRequired,
    fetchingLocalities: bool.isRequired,
    cuit: string.isRequired,
    complementaryDataselectorsRequest: arrayOf(shape({})).isRequired,
};

const mapStateToProps = (state) => ({
    generalDataBackup: environmentSelectors.getGeneralDataBackup(state),
    fetchingLocalities: onboardingSelectors.getFetching(state),
    generalDataRequest: environmentSelectors.getGeneralDataRequest(state),
    cuit: onboardingSelectors.getDocumentNumber(state),
    complementaryDataselectorsRequest: environmentSelectors.getComplementaryDataSelectorsRequest(state),
});

export default connect(mapStateToProps)(StepGeneralDataRefactor);
